import * as React from 'react'

import { Icon } from '@factorialco/deprecated-design-system'
import cn from 'classnames/bind'
import _ from 'lodash'

import { observer } from 'mobx-react'

import styles from './index.module.scss'

interface Props {
  items: Array<Record<string, any>>
  selectedIndex: number
  onSelectIndex: (value: number) => void
  renderItem: (index: number) => React.ReactNode
}
// TODO: check this type after TS migration
// type RefCurrent = React.ElementRef<any>
type Rect = DOMRect
interface Direction {
  left: number
  right: number
}

const cx = cn.bind(styles)

const NavigationSlider: React.FC<Props> = ({ items, selectedIndex, onSelectIndex, renderItem }) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const middleRef = React.useRef<HTMLDivElement>(null)
  const [itemsRefs, setItemsRefs] = React.useState<React.RefObject<HTMLDivElement>[]>(() =>
    items.map(() => React.createRef<HTMLDivElement>())
  )
  const [rightArrow, setRightArrow] = React.useState(false)
  const [leftArrow, setLeftArrow] = React.useState(false)
  const [currentPosition, setCurrentPosition] = React.useState(0)

  const directions: Direction = React.useMemo(
    () => ({
      right: -200,
      left: 200,
    }),
    []
  )

  const padding = 20

  const getMostRightRect = (): Rect | undefined => {
    const lastRef = _.last(itemsRefs)
    return lastRef?.current?.getBoundingClientRect()
  }

  const getMostLeftRect = (): Rect | undefined => {
    return itemsRefs[0]?.current?.getBoundingClientRect()
  }

  const getWrapperWidth = (): number => {
    const mostRightRect = getMostRightRect()
    const mostLeftRect = getMostLeftRect()
    if (!mostRightRect || !mostLeftRect) return 0
    return mostRightRect.right - mostLeftRect.left
  }

  const getMiddleWidth = (): number => {
    return middleRef.current?.offsetWidth ?? 0
  }

  const getMiddleOverflows = (): boolean => {
    return getWrapperWidth() > getMiddleWidth()
  }

  const getDistanceToEnd = (position: number): number => {
    const total = getMiddleWidth() - getWrapperWidth() - padding
    return total - position
  }

  const setWrapperStyle = React.useCallback((newPosition: number) => {
    const wrapper = wrapperRef.current
    if (wrapper) {
      wrapper.style.transform = `translatex(${newPosition}px)`
    }
  }, [])

  const displayRightArrow = React.useCallback(
    (amount: number): boolean => {
      const middle = middleRef.current?.getBoundingClientRect()
      const mostRightRect = getMostRightRect()
      if (!middle || !mostRightRect) return false

      const rightLimit = middle.x + middle.width
      const middleOverflows = getMiddleOverflows()

      if (rightLimit > mostRightRect.right + amount || !middleOverflows) {
        setRightArrow(false)
        return false
      } else {
        setRightArrow(true)
        return true
      }
    },
    [itemsRefs]
  )

  const displayLeftArrow = React.useCallback(
    (amount: number): boolean => {
      const middle = middleRef.current?.getBoundingClientRect()
      const mostLeftRect = getMostLeftRect()
      if (!middle || !mostLeftRect) return false

      const middleOverflows = getMiddleOverflows()

      if (middle.x <= mostLeftRect.left + amount || !middleOverflows) {
        setLeftArrow(false)
        return false
      } else {
        setLeftArrow(true)
        return true
      }
    },
    [itemsRefs]
  )

  const realignSlider = React.useCallback(() => {
    setWrapperStyle(0)
    displayRightArrow(0)
    displayLeftArrow(0)
  }, [setWrapperStyle, displayRightArrow, displayLeftArrow])

  const move = React.useCallback(
    (amount: number) => {
      setCurrentPosition((currentPosition) => {
        let newPosition = currentPosition
        const middleOverflows = getMiddleOverflows()
        const middleWidth = getMiddleWidth()
        const wrapperWidth = getWrapperWidth()
        const distanceToEnd = getDistanceToEnd(currentPosition)

        // We don't care about moving if
        // (1) the total width is not big enough or
        // (2) is the first item
        if (!middleOverflows || !displayLeftArrow(amount)) {
          newPosition = 0 // Don't move to the right if it is the last item
        } else if (!displayRightArrow(amount)) {
          newPosition = Math.min(middleWidth - wrapperWidth - padding, newPosition, distanceToEnd)
        } else {
          newPosition += amount
        }

        setWrapperStyle(newPosition)
        return newPosition
      })
    },
    [displayLeftArrow, displayRightArrow, setWrapperStyle]
  )

  const selectIndex = React.useCallback(
    (index: number) => {
      if (!itemsRefs[index]?.current) return

      const rect = itemsRefs[index].current?.getBoundingClientRect()
      const middleRect = middleRef.current?.getBoundingClientRect()

      if (!rect || !middleRect) return

      const middleWidth = getMiddleWidth()
      const distance = middleRect.left + middleWidth / 2 - rect.left - rect.width / 2
      move(distance)
    },
    [itemsRefs, move]
  )

  const onClickIndex = React.useCallback(
    (index: number) => {
      onSelectIndex(index)
      selectIndex(index)
    },
    [onSelectIndex, selectIndex]
  )

  // Handle items length changes
  React.useEffect(() => {
    if (itemsRefs.length !== items.length) {
      setItemsRefs(items.map(() => React.createRef<HTMLDivElement>()))
    }
  }, [items.length, itemsRefs.length])

  // Handle selectedIndex changes
  React.useEffect(() => {
    selectIndex(selectedIndex)
  }, [selectedIndex, selectIndex])

  // Initialize on mount and setup resize listener
  React.useEffect(() => {
    realignSlider()
    window.addEventListener('resize', realignSlider)

    return () => {
      window.removeEventListener('resize', realignSlider)
    }
  }, [realignSlider])

  return (
    <div className={styles.cardSlider}>
      <div className={cx('left', leftArrow && 'visible')} onClick={() => move(directions.left)}>
        <div className={cx('arrow', leftArrow && 'visible')}>
          <Icon.Medium icon="arrow-left" color="radicalRed1000" />
        </div>
      </div>
      <div className={styles.middle} ref={middleRef}>
        <div className={styles.wrapper} ref={wrapperRef}>
          {items.map((_item: any, index: number) => (
            <div
              className={styles.itemWrapper}
              key={index}
              ref={itemsRefs[index]}
              onClick={() => onClickIndex(index)}
            >
              {renderItem(index)}
            </div>
          ))}
        </div>
      </div>
      <div
        className={cx('right', rightArrow && 'visible')}
        onClick={() => rightArrow && move(directions.right)}
      >
        <div className={cx('arrow', rightArrow && 'visible')}>
          <Icon.Medium icon="arrow-right" color="radicalRed1000" />
        </div>
      </div>
    </div>
  )
}

export default observer(NavigationSlider)
